import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { PageHeader, Card, Button, ProgressBar } from './ui';

// ---------------------------------------------------------------------------
// Types (mirror /ui/admin/lender-export responses)
// ---------------------------------------------------------------------------

export interface SpellingCount {
  spelling: string;
  live: number;
  cancelled: number;
  eligible?: number;
  reason?: string;
}

export interface FamilyPreview {
  email: string;
  canonicals: string[];
  aliases: string[];
  inputs: { input: string; matchedVia: string }[];
  matchedSpellings: SpellingCount[];
  suggestedSpellings: SpellingCount[];
  suggestedTruncated: number;
  totals: { live: number; cancelled: number; eligible?: number };
}

export interface PreviewResponse {
  families: FamilyPreview[];
  unresolved: { input: string; choices: { name: string; email: string }[] }[];
}

export interface LenderOption {
  name: string;
  email: string;
  type: 'canonical' | 'alias';
  canonicalName: string;
}

interface LenderOptionsResponse {
  lenders: LenderOption[];
}

export interface TemplateColumn {
  header: string;
  source: string;
  filled: boolean;
}

export interface TemplateOption {
  key: string;
  name: string;
  note: string;
  columns: TemplateColumn[];
}

export interface FieldOption {
  key: string;
  label: string;
  group: string;
}

export interface Preset {
  id: string;
  name: string;
  fields: string[];
}

export interface ClaimsListFamily {
  withResponses?: number;
  email: string;
  lenderName: string;
  spellings: string[];
  count: number;
  claimIds: string[];
}

export interface ClaimsListResponse {
  withResponses?: number;
  total: number;
  families: ClaimsListFamily[];
}

export interface MarkSummary {
  marked: string[];
  alreadySubmitted: string[];
  cancelled: string[];
  notInSelection: string[];
  notFound: string[];
  conflict: string[];
  failed: string[];
  sentDate?: string;
}

export interface AllLendersCounts {
  total: number;
  live: number;
  cancelled: number;
  submitted: number;
  unsubmitted: number;
  liveSubmitted?: number;
  liveUnsubmitted?: number;
}

export interface AllLendersJob {
  jobId: string;
  status: 'queued' | 'running' | 'done' | 'failed';
  stage: string;
  total: number | null;
  written: number;
  rows?: number;
  filename?: string;
  sizeBytes?: number;
  downloadUrl?: string;
  expiresAt?: string;
  error?: string;
  startedAt: string;
  finishedAt?: string;
}

const MAX_SUGGESTIONS = 8;
const MARK_BATCH_SIZE = 200;
// How often the review step asks the server how an all-lenders build is going.
const ALL_JOB_POLL_MS = 2000;
// Consecutive failed progress checks before the build is shown as lost (a deploy or a
// blip on one request must not end the watch while the server is still building).
const ALL_JOB_POLL_MAX_FAILURES = 3;
const ALL_JOB_STAGES: Record<string, string> = {
  queued: 'Waiting to start…',
  counting: 'Counting claims…',
  writing: 'Writing claims…',
  saving: 'Saving the workbook…',
  uploading: 'Storing the file…',
};

type Step = 1 | 2 | 3 | 4;
type LayoutMode = 'template' | 'custom';

type SubmissionFilter = 'unsubmitted' | 'all' | 'submitted';
const SUBMISSION_LABELS: Record<SubmissionFilter, string> = {
  unsubmitted: 'not yet bulk-submitted',
  all: 'bulk-submitted or not',
  submitted: 'already bulk-submitted only',
};

const STEPS: { n: Step; label: string }[] = [
  { n: 1, label: 'Lenders' },
  { n: 2, label: 'Claims' },
  { n: 3, label: 'Layout' },
  { n: 4, label: 'Review' },
];

const formatNumber = (n: number): string => new Intl.NumberFormat('en-GB').format(n);

const formatDay = (iso: string): string => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return y && m && d ? `${d}/${m}/${y}` : iso;
};

const emptyMarkSummary = (): MarkSummary => ({
  marked: [], alreadySubmitted: [], cancelled: [], notInSelection: [], notFound: [], conflict: [], failed: [],
});

const formatBytes = (n: number): string => (n >= 1_000_000 ? `${(n / 1_000_000).toFixed(0)} MB` : `${Math.max(1, Math.round(n / 1000))} KB`);

const formatStamp = (iso?: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const filenameFromDisposition = (header: string | null, fallback: string): string => {
  if (!header) return fallback;
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (utf8) return decodeURIComponent(utf8[1]);
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain ? plain[1] : fallback;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const LenderClaimsExport: React.FC = () => {
  const { apiRequest } = useAuth();

  // ---- wizard
  const [step, setStep] = useState<Step>(1);
  const [furthestStep, setFurthestStep] = useState<Step>(1);
  const [error, setError] = useState('');

  // ---- step 1: lenders
  const [namesInput, setNamesInput] = useState('');
  const [lenderOptions, setLenderOptions] = useState<LenderOption[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeSuggestion, setActiveSuggestion] = useState(-1);
  const [caret, setCaret] = useState<number | null>(null);
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  // the names the current preview was resolved from - later calls use these,
  // never the live input, so an edited-but-unresolved input can't produce
  // a mismatched names/spellings request
  const [previewNames, setPreviewNames] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const blurTimer = useRef<number | null>(null);
  const previewSeq = useRef(0);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // ---- step 2: claims
  const [includeCancelled, setIncludeCancelled] = useState(false);
  const [submission, setSubmission] = useState<SubmissionFilter>('unsubmitted');
  const [includeResponses, setIncludeResponses] = useState(true);
  const [openedFrom, setOpenedFrom] = useState('');
  const [openedTo, setOpenedTo] = useState('');
  const [countsStale, setCountsStale] = useState(false);

  // ---- step 3: layout
  const [mode, setMode] = useState<LayoutMode>('template');
  const [templates, setTemplates] = useState<TemplateOption[]>([]);
  const [templateKey, setTemplateKey] = useState('');
  const [fields, setFields] = useState<FieldOption[]>([]);
  const [chosenFields, setChosenFields] = useState<string[]>([]);
  const [presets, setPresets] = useState<Preset[]>([]);
  const [presetName, setPresetName] = useState('');
  const [presetBusy, setPresetBusy] = useState(false);
  const [presetMessage, setPresetMessage] = useState('');

  // ---- step 4: review / download / mark
  const [claimsList, setClaimsList] = useState<ClaimsListResponse | null>(null);
  const [claimsLoading, setClaimsLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadResult, setDownloadResult] = useState('');
  const [downloadedFor, setDownloadedFor] = useState<ClaimsListResponse | null>(null);
  const [markConfirmOpen, setMarkConfirmOpen] = useState(false);
  const [marking, setMarking] = useState(false);
  const [markProgress, setMarkProgress] = useState({ done: 0, total: 0 });
  const [markSummary, setMarkSummary] = useState<MarkSummary | null>(null);

  // ---- all lenders (every claim, every lender; built in the background)
  const [allLenders, setAllLenders] = useState(false);
  const [allCounts, setAllCounts] = useState<AllLendersCounts | null>(null);
  // the whole book (live + cancelled, submitted or not) so step 1 shows the full picture
  const [allOverview, setAllOverview] = useState<AllLendersCounts | null>(null);
  const [allCountsLoading, setAllCountsLoading] = useState(false);
  const [allJob, setAllJob] = useState<AllLendersJob | null>(null);
  const [allStarting, setAllStarting] = useState(false);
  const [recentJobs, setRecentJobs] = useState<AllLendersJob[]>([]);

  const names = useMemo(
    () => namesInput.split(/[\n,]/).map((n) => n.trim()).filter(Boolean),
    [namesInput]
  );

  const filters = useMemo(
    () => ({ includeCancelled, submission, openedFrom, openedTo }),
    [includeCancelled, submission, openedFrom, openedTo]
  );
  // Lender responses only exist on claims that have been bulk-submitted, so the sheet is offered
  // only when the claim choice can include them.
  const responsesPossible = submission !== 'unsubmitted';
  const responsesWanted = responsesPossible && includeResponses;

  // ------------------------------------------------------------------
  // Reference data: lenders typeahead, templates, field catalogue, presets
  // ------------------------------------------------------------------
  useEffect(() => {
    const load = async () => {
      try {
        const data = await apiRequest<LenderOptionsResponse>({ url: '/ui/admin/lender-export/lenders', method: 'GET' });
        setLenderOptions(data.lenders ?? []);
      } catch (err) {
        // typeahead is a convenience - typing still works without it
        console.error('Failed to load lender options:', err);
      }
      try {
        const data = await apiRequest<{ templates: TemplateOption[] }>({ url: '/ui/admin/lender-export/templates', method: 'GET' });
        setTemplates(data.templates ?? []);
      } catch (err) {
        console.error('Failed to load templates:', err);
      }
      try {
        const data = await apiRequest<{ fields: FieldOption[] }>({ url: '/ui/admin/lender-export/fields', method: 'GET' });
        setFields(data.fields ?? []);
      } catch (err) {
        console.error('Failed to load fields:', err);
      }
      try {
        const data = await apiRequest<{ presets: Preset[] }>({ url: '/ui/admin/lender-export/presets', method: 'GET' });
        setPresets(data.presets ?? []);
      } catch (err) {
        console.error('Failed to load presets:', err);
      }
      try {
        const data = await apiRequest<{ jobs: AllLendersJob[] }>({ url: '/ui/admin/lender-export/all/jobs', method: 'GET' });
        setRecentJobs(data.jobs ?? []);
      } catch (err) {
        // the list of earlier builds is a convenience - the wizard works without it
        console.error('Failed to load recent all-lenders exports:', err);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cancel any pending blur-close timer on unmount
  useEffect(() => () => {
    if (blurTimer.current !== null) window.clearTimeout(blurTimer.current);
  }, []);

  // ------------------------------------------------------------------
  // Typeahead: suggestions apply to the comma-segment the CARET is in
  // ------------------------------------------------------------------
  const segmentBounds = useMemo(() => {
    const pos = Math.min(caret ?? namesInput.length, namesInput.length);
    let start = 0;
    for (let i = pos - 1; i >= 0; i--) {
      if (namesInput[i] === ',' || namesInput[i] === '\n') {
        start = i + 1;
        break;
      }
    }
    let end = namesInput.length;
    for (let i = pos; i < namesInput.length; i++) {
      if (namesInput[i] === ',' || namesInput[i] === '\n') {
        end = i;
        break;
      }
    }
    return { start, end };
  }, [namesInput, caret]);

  const currentSegment = namesInput.slice(segmentBounds.start, segmentBounds.end).trim();

  const suggestions = useMemo(() => {
    const query = currentSegment.toLowerCase();
    if (!query) return [];
    return lenderOptions
      .filter((o) =>
        (o.name.toLowerCase().includes(query) || o.email.toLowerCase().includes(query)) &&
        // an exact self-match would be a no-op selection - don't reopen
        // the dropdown over a segment that's already a completed pick
        o.name.toLowerCase() !== query)
      .slice(0, MAX_SUGGESTIONS);
  }, [lenderOptions, currentSegment]);

  const suggestionsOpen = showSuggestions && suggestions.length > 0;

  const selectSuggestion = (option: LenderOption) => {
    const { start, end } = segmentBounds;
    const before = namesInput.slice(0, start);
    const after = namesInput.slice(end);
    const lead = before && !before.endsWith(' ') ? ' ' : '';
    const inserted = `${before}${lead}${option.name}`;
    setNamesInput(inserted + after);
    setShowSuggestions(false);
    setActiveSuggestion(-1);
    setCaret(inserted.length);
    window.setTimeout(() => {
      inputRef.current?.setSelectionRange(inserted.length, inserted.length);
      inputRef.current?.focus();
    }, 0);
  };

  // ------------------------------------------------------------------
  // Preview (resolve the lender families + spelling census)
  // ------------------------------------------------------------------
  const runPreview = useCallback(async (requestNames: string[], keepTicks: boolean) => {
    if (requestNames.length === 0) return;
    // latest-wins: a stale response must never overwrite a newer preview
    const seq = ++previewSeq.current;
    setShowSuggestions(false);
    setPreviewLoading(true);
    setError('');
    try {
      const data = await apiRequest<PreviewResponse>({
        url: '/ui/admin/lender-export/preview',
        method: 'POST',
        data: { names: requestNames, ...filters },
      });
      if (seq !== previewSeq.current) return;
      setPreview(data);
      setPreviewNames(requestNames);
      setCountsStale(false);
      if (!keepTicks) {
        // Matched spellings start ticked; suggestions start unticked.
        const ticked = new Set<string>();
        data.families.forEach((f) => f.matchedSpellings.forEach((s) => ticked.add(s.spelling)));
        setSelected(ticked);
      }
    } catch (err: any) {
      console.error('Lender export preview error:', err);
      if (seq === previewSeq.current) setError(err.message || 'Failed to resolve lender');
    } finally {
      if (seq === previewSeq.current) setPreviewLoading(false);
    }
  }, [apiRequest, filters]);

  const handleResolve = () => {
    if (names.length === 0 || previewLoading) return;
    setAllLenders(false);
    setAllCounts(null);
    setAllOverview(null);
    setAllJob(null);
    setPreview(null);
    setClaimsList(null);
    setDownloadResult('');
    setDownloadedFor(null);
    setMarkSummary(null);
    setConfirmed(false);
    setFurthestStep(1);
    runPreview(names, false);
  };

  const handleNamesKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (suggestionsOpen && e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveSuggestion((i) => (i + 1) % suggestions.length);
    } else if (suggestionsOpen && e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveSuggestion((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
      setActiveSuggestion(-1);
    } else if (e.key === 'Enter') {
      if (suggestionsOpen && activeSuggestion >= 0) {
        e.preventDefault();
        selectSuggestion(suggestions[activeSuggestion]);
      } else if (suggestionsOpen && suggestions.length === 1) {
        // a single visible option is unambiguous - Enter picks it
        e.preventDefault();
        selectSuggestion(suggestions[0]);
      } else {
        setShowSuggestions(false);
        handleResolve();
      }
    }
  };

  const toggleSpelling = (spelling: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(spelling)) next.delete(spelling);
      else next.add(spelling);
      return next;
    });
    setClaimsList(null);
    setConfirmed(false);
  };

  // Any change to the claim filters makes the counts and claim list stale.
  const setFilter = (apply: () => void) => {
    apply();
    setCountsStale(true);
    setClaimsList(null);
    setConfirmed(false);
  };

  const loadAllCounts = useCallback(async () => {
    setAllCountsLoading(true);
    setError('');
    try {
      const [data, overview] = await Promise.all([
        apiRequest<AllLendersCounts>({
          url: '/ui/admin/lender-export/all/counts',
          method: 'POST',
          data: { ...filters },
        }),
        apiRequest<AllLendersCounts>({
          url: '/ui/admin/lender-export/all/counts',
          method: 'POST',
          data: { ...filters, includeCancelled: true, submission: 'all' },
        }),
      ]);
      setAllCounts(data);
      setAllOverview(overview);
      setCountsStale(false);
    } catch (err: any) {
      console.error('All-lenders counts error:', err);
      setError(err.message || 'Failed to count the claims');
    } finally {
      setAllCountsLoading(false);
    }
  }, [apiRequest, filters]);

  useEffect(() => {
    if (allLenders) loadAllCounts();
  }, [allLenders, loadAllCounts]);

  // "All lenders": no lender resolution, no spelling census - every claim the filters select.
  const handleAllLenders = () => {
    if (allCountsLoading || previewLoading) return;
    setAllLenders(true);
    setPreview(null);
    setPreviewNames([]);
    setSelected(new Set());
    setNamesInput('');
    setShowSuggestions(false);
    setClaimsList(null);
    setDownloadResult('');
    setDownloadedFor(null);
    setMarkSummary(null);
    setConfirmed(false);
    setAllJob(null);
    setFurthestStep(1);
  };

  const refreshCounts = () => (allLenders ? loadAllCounts() : runPreview(previewNames, true));

  // ------------------------------------------------------------------
  // Derived selection
  // ------------------------------------------------------------------
  const families = useMemo(() => preview?.families ?? [], [preview]);
  const distinctEmails = useMemo(
    () => new Set(families.map((f) => f.email.toLowerCase()).filter(Boolean)),
    [families]
  );

  const selectedCounts = useMemo(() => {
    let live = 0;
    let cancelled = 0;
    let eligible = 0;
    families.forEach((f) => {
      [...f.matchedSpellings, ...f.suggestedSpellings].forEach((s) => {
        if (selected.has(s.spelling)) {
          live += s.live;
          cancelled += s.cancelled;
          eligible += s.eligible ?? s.live;
        }
      });
    });
    return { live, cancelled, eligible };
  }, [families, selected]);

  const familyEligible = (f: FamilyPreview): number =>
    [...f.matchedSpellings, ...f.suggestedSpellings]
      .filter((s) => selected.has(s.spelling))
      .reduce((sum, s) => sum + (s.eligible ?? s.live), 0);

  const template = templates.find((t) => t.key === templateKey) || null;
  const fieldLabel = (key: string) => fields.find((f) => f.key === key)?.label ?? key;

  const layoutReady = mode === 'template' ? Boolean(templateKey) : chosenFields.length > 0;
  const step1Ready = allLenders
    ? Boolean(allCounts && allCounts.total > 0)
    : families.length > 0 && selected.size > 0;
  const eligibleTotal = allLenders ? (allCounts?.total ?? 0) : selectedCounts.eligible;

  // All lenders has no separate Claims step: the claim choice lives in its step-1 card.
  const canVisit = (n: Step) => n <= furthestStep && !(allLenders && n === 2);

  const goTo = (n: Step) => {
    if (!canVisit(n)) return;
    setStep(n);
    setError('');
  };

  const next = () => {
    const n = Math.min(step + 1 + (allLenders && step === 1 ? 1 : 0), 4) as Step;
    setStep(n);
    setFurthestStep((prev) => (n > prev ? n : prev));
    setError('');
  };

  // ------------------------------------------------------------------
  // Step 3: presets
  // ------------------------------------------------------------------
  const toggleField = (key: string) => {
    setChosenFields((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const moveField = (key: string, delta: -1 | 1) => {
    setChosenFields((prev) => {
      const i = prev.indexOf(key);
      const j = i + delta;
      if (i < 0 || j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };

  const savePreset = async () => {
    if (!presetName.trim() || chosenFields.length === 0 || presetBusy) return;
    setPresetBusy(true);
    setPresetMessage('');
    try {
      const data = await apiRequest<{ preset: Preset }>({
        url: '/ui/admin/lender-export/presets',
        method: 'POST',
        data: { name: presetName.trim(), fields: chosenFields },
      });
      setPresets((prev) => [...prev.filter((p) => p.id !== data.preset.id), data.preset]);
      setPresetMessage(`Saved "${data.preset.name}".`);
      setPresetName('');
    } catch (err: any) {
      setPresetMessage(err.message || 'Failed to save preset');
    } finally {
      setPresetBusy(false);
    }
  };

  const deletePreset = async (preset: Preset) => {
    if (presetBusy) return;
    setPresetBusy(true);
    setPresetMessage('');
    try {
      await apiRequest({ url: `/ui/admin/lender-export/presets/${encodeURIComponent(preset.id)}`, method: 'DELETE' });
      setPresets((prev) => prev.filter((p) => p.id !== preset.id));
      setPresetMessage(`Deleted "${preset.name}".`);
    } catch (err: any) {
      setPresetMessage(err.message || 'Failed to delete preset');
    } finally {
      setPresetBusy(false);
    }
  };

  // ------------------------------------------------------------------
  // Step 4: exact claim list for the selection
  // ------------------------------------------------------------------
  const selectionBody = useMemo(
    () => ({ names: previewNames, spellings: Array.from(selected), ...filters }),
    [previewNames, selected, filters]
  );

  const loadClaimsList = useCallback(async () => {
    setClaimsLoading(true);
    setError('');
    try {
      const data = await apiRequest<ClaimsListResponse>({
        url: '/ui/admin/lender-export/claims',
        method: 'POST',
        data: selectionBody,
      });
      setClaimsList(data);
    } catch (err: any) {
      setError(err.message || 'Failed to count the claims');
    } finally {
      setClaimsLoading(false);
    }
  }, [apiRequest, selectionBody]);

  useEffect(() => {
    if (step === 4 && !allLenders && !claimsList && !claimsLoading) loadClaimsList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, claimsList]);

  // ------------------------------------------------------------------
  // All lenders: start the background build, then poll it
  // ------------------------------------------------------------------
  const allJobBusy = Boolean(allJob && (allJob.status === 'queued' || allJob.status === 'running'));
  const pollFailures = useRef(0);

  const handleBuildAll = async () => {
    if (!allLenders || !allCounts || allCounts.total === 0 || !confirmed || allStarting || allJobBusy) return;
    setAllStarting(true);
    setError('');
    try {
      const layout = mode === 'template'
        ? { mode: 'template', template: templateKey, includeResponses: responsesWanted }
        : { mode: 'custom', fields: chosenFields, includeResponses: responsesWanted };
      const job = await apiRequest<AllLendersJob>({
        url: '/ui/admin/lender-export/all/build',
        method: 'POST',
        data: { ...filters, ...layout },
      });
      pollFailures.current = 0;
      setAllJob(job);
      setRecentJobs((prev) => [job, ...prev.filter((j) => j.jobId !== job.jobId)]);
    } catch (err: any) {
      console.error('All-lenders build error:', err);
      setError(err.message || 'Failed to start the export');
    } finally {
      setAllStarting(false);
    }
  };

  useEffect(() => {
    if (!allJob || (allJob.status !== 'queued' && allJob.status !== 'running')) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const job = await apiRequest<AllLendersJob>({ url: `/ui/admin/lender-export/all/jobs/${allJob.jobId}`, method: 'GET' });
        if (!cancelled) {
          pollFailures.current = 0;
          setAllJob(job);
          setRecentJobs((prev) => prev.map((j) => (j.jobId === job.jobId ? job : j)));
        }
      } catch (err: any) {
        if (cancelled) return;
        pollFailures.current += 1;
        if (pollFailures.current >= ALL_JOB_POLL_MAX_FAILURES) {
          setAllJob({ ...allJob, status: 'failed', stage: 'failed', error: err.message || 'Lost track of the export' });
        }
      }
    };
    const timer = window.setInterval(tick, ALL_JOB_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [allJob, apiRequest]);

  // ------------------------------------------------------------------
  // Download (authed fetch -> blob)
  // ------------------------------------------------------------------
  const handleDownload = async () => {
    if (!claimsList || claimsList.total === 0 || !confirmed || downloading) return;
    setDownloading(true);
    setDownloadResult('');
    setError('');
    try {
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4000';
      const layout = mode === 'template'
        ? { mode: 'template', template: templateKey, includeResponses: responsesWanted }
        : { mode: 'custom', fields: chosenFields, includeResponses: responsesWanted };
      const response = await fetch(`${apiUrl}/ui/admin/lender-export/download`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...selectionBody, ...layout }),
      });

      if (!response.ok) {
        let message = `Export failed (HTTP ${response.status})`;
        try {
          const errorData = await response.json();
          message = errorData.error || message;
        } catch {
          // non-JSON error body - keep the HTTP message
        }
        throw new Error(message);
      }

      const blob = await response.blob();
      const date = new Date().toISOString().slice(0, 10);
      const filename = filenameFromDisposition(response.headers.get('Content-Disposition'), `Lender_export_${date}.xlsx`);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      const count = response.headers.get('X-Export-Claim-Count') ?? String(claimsList.total);
      setDownloadResult(`Downloaded ${filename} (${formatNumber(Number(count))} claims).`);
      setDownloadedFor(claimsList);
      setMarkSummary(null);
    } catch (err: any) {
      console.error('Lender export download error:', err);
      setError(err.message || 'Failed to download export');
    } finally {
      setDownloading(false);
    }
  };

  // ------------------------------------------------------------------
  // Mark as sent to lender (chunked, with progress)
  // ------------------------------------------------------------------
  const handleMarkSubmitted = async () => {
    if (!downloadedFor || marking) return;
    setMarkConfirmOpen(false);
    setMarking(true);
    setError('');
    const total = downloadedFor.families.reduce((n, f) => n + f.claimIds.length, 0);
    setMarkProgress({ done: 0, total });
    const summary = emptyMarkSummary();
    const exportTag = `lender-export-${new Date().toISOString().slice(0, 16).replace(/[:T]/g, '-')}`;
    try {
      for (const family of downloadedFor.families) {
        for (let i = 0; i < family.claimIds.length; i += MARK_BATCH_SIZE) {
          const batch = family.claimIds.slice(i, i + MARK_BATCH_SIZE);
          const part = await apiRequest<MarkSummary>({
            url: '/ui/admin/lender-export/mark-submitted',
            method: 'POST',
            data: {
              claimIds: batch,
              spellings: family.spellings,
              lenderName: family.lenderName,
              lenderEmail: family.email,
              exportTag,
            },
          });
          (Object.keys(summary) as (keyof MarkSummary)[]).forEach((k) => {
            if (k !== 'sentDate') summary[k].push(...(part[k] ?? []));
          });
          summary.sentDate = part.sentDate ?? summary.sentDate;
          setMarkProgress({ done: Math.min(i + batch.length, family.claimIds.length) + doneBefore(downloadedFor, family), total });
        }
      }
      setMarkSummary({ ...summary });
    } catch (err: any) {
      setError(err.message || 'Marking stopped part-way - the summary shows what was done');
      setMarkSummary({ ...summary });
    } finally {
      setMarking(false);
    }
  };

  const doneBefore = (list: ClaimsListResponse, family: ClaimsListFamily): number => {
    let n = 0;
    for (const f of list.families) {
      if (f === family) break;
      n += f.claimIds.length;
    }
    return n;
  };

  // ------------------------------------------------------------------
  // Render helpers
  // ------------------------------------------------------------------
  const renderSpellingRow = (s: SpellingCount, suggested: boolean) => (
    <tr key={s.spelling} className="border-t border-gray-100">
      <td className="py-1.5 pr-2">
        <input
          type="checkbox"
          checked={selected.has(s.spelling)}
          onChange={() => toggleSpelling(s.spelling)}
          className="rounded text-teal-600 focus:ring-teal-500"
          aria-label={`Include ${s.spelling}`}
        />
      </td>
      <td className="py-1.5 pr-4 font-mono text-xs">{s.spelling}</td>
      <td className="py-1.5 pr-4 text-right tabular-nums">{formatNumber(s.live)}</td>
      <td className="py-1.5 pr-4 text-right tabular-nums text-gray-500">{formatNumber(s.cancelled)}</td>
      <td className="py-1.5 text-xs text-gray-500 italic">{suggested ? s.reason : ''}</td>
    </tr>
  );

  const spellingTable = (family: FamilyPreview) => (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-gray-500">
            <th className="py-1 pr-2 font-medium" aria-label="Include" />
            <th className="py-1 pr-4 font-medium">Lender name on claims</th>
            <th className="py-1 pr-4 font-medium text-right">Live</th>
            <th className="py-1 pr-4 font-medium text-right">Cancelled</th>
            <th className="py-1 font-medium" />
          </tr>
        </thead>
        <tbody>
          {family.matchedSpellings.map((s) => renderSpellingRow(s, false))}
          {family.suggestedSpellings.length > 0 && (
            <tr>
              <td colSpan={5} className="pt-3 pb-1 text-xs font-medium text-amber-700">
                Possible unmapped spellings — not in the lender mapping, tick to include
              </td>
            </tr>
          )}
          {family.suggestedSpellings.map((s) => renderSpellingRow(s, true))}
        </tbody>
      </table>
      {family.suggestedTruncated > 0 && (
        <p className="mt-1 text-xs text-amber-600">
          {family.suggestedTruncated} further possible spelling{family.suggestedTruncated !== 1 ? 's' : ''} not shown.
        </p>
      )}
    </div>
  );

  const familyHeading = (family: FamilyPreview) => (
    <div className="flex flex-wrap items-baseline gap-2 mb-1">
      <h3 className="text-base font-semibold text-gray-800">{family.canonicals.join(' · ')}</h3>
      {family.email ? (
        <span className="text-xs bg-teal-50 text-teal-700 border border-teal-200 rounded-full px-2 py-0.5">
          {family.email}
        </span>
      ) : (
        <span className="text-xs bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-2 py-0.5">
          no lender email on file
        </span>
      )}
    </div>
  );

  const stepSummary = (n: Step): string => {
    if (n === 1) {
      if (!step1Ready) return '';
      return allLenders ? 'All lenders' : families.map((f) => f.canonicals[0]).join(', ');
    }
    if (n === 2) {
      const parts = [includeCancelled ? 'live + cancelled' : 'live only', SUBMISSION_LABELS[submission]];
      if (openedFrom || openedTo) parts.push(`opened ${openedFrom ? formatDay(openedFrom) : '…'} – ${openedTo ? formatDay(openedTo) : '…'}`);
      return parts.join(' · ');
    }
    if (n === 3) {
      if (!layoutReady) return '';
      return mode === 'template' ? `${template?.name ?? templateKey} template` : `${chosenFields.length} chosen fields`;
    }
    if (allLenders) return allCounts ? `${formatNumber(allCounts.total)} claims` : '';
    return claimsList ? `${formatNumber(claimsList.total)} claims` : '';
  };

  const allCountsTable = allCounts ? (
    <table className="text-sm" aria-label="All lenders claim counts">
      <tbody>
        <tr><td className="pr-6 py-1 text-gray-600">Live claims</td><td className="py-1 text-right tabular-nums font-medium">{formatNumber(allCounts.live)}</td></tr>
        {includeCancelled && (
          <tr><td className="pr-6 py-1 text-gray-600">Cancelled claims</td><td className="py-1 text-right tabular-nums font-medium">{formatNumber(allCounts.cancelled)}</td></tr>
        )}
        {submission !== 'submitted' && (
          <tr><td className="pr-6 py-1 text-gray-600">Not yet bulk-submitted</td><td className="py-1 text-right tabular-nums">{formatNumber(allCounts.unsubmitted)}</td></tr>
        )}
        {submission !== 'unsubmitted' && (
          <tr><td className="pr-6 py-1 text-gray-600">Already bulk-submitted</td><td className="py-1 text-right tabular-nums">{formatNumber(allCounts.submitted)}</td></tr>
        )}
        <tr className="border-t border-gray-200 font-semibold"><td className="pr-6 py-1">Total to export</td><td className="py-1 text-right tabular-nums">{formatNumber(allCounts.total)}</td></tr>
      </tbody>
    </table>
  ) : null;

  const allOverviewTable = allOverview && allCounts ? (
    <div>
      <table className="text-sm" aria-label="All lenders overview">
        <tbody>
          <tr>
            <td className="pr-6 py-1 text-gray-700 font-medium">Live claims</td>
            <td className="py-1 text-right tabular-nums font-semibold">{formatNumber(allOverview.live)}</td>
          </tr>
          <tr>
            <td className="pr-6 py-1 pl-4 text-gray-600">of which not yet bulk-submitted</td>
            <td className="py-1 text-right tabular-nums">{formatNumber(allOverview.liveUnsubmitted ?? allOverview.unsubmitted)}</td>
          </tr>
          <tr>
            <td className="pr-6 py-1 pl-4 text-gray-600">of which already bulk-submitted</td>
            <td className="py-1 text-right tabular-nums">{formatNumber(allOverview.liveSubmitted ?? allOverview.submitted)}</td>
          </tr>
          <tr>
            <td className="pr-6 py-1 text-gray-700 font-medium">Cancelled claims</td>
            <td className="py-1 text-right tabular-nums font-semibold">{formatNumber(allOverview.cancelled)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  ) : null;

  // The claim choice for an all-lenders export lives here, not on a separate step.
  const allFilterControls = (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4 pt-4 border-t border-gray-100">
      <div className="space-y-3">
        <fieldset>
          <legend className="text-sm font-medium text-gray-700 mb-1">Which claims</legend>
          {([
            ['unsubmitted', 'Not yet bulk-submitted', 'What still needs submitting'],
            ['all', 'Everything, bulk-submitted or not', 'The full export - also unlocks the Lender responses sheet'],
            ['submitted', 'Already bulk-submitted only', 'The reconciliation view'],
          ] as [SubmissionFilter, string, string][]).map(([value, label, hint]) => (
            <label key={value} className="flex items-start gap-2 cursor-pointer mb-1.5">
              <input
                type="radio"
                name="all-submission-filter"
                checked={submission === value}
                onChange={() => setFilter(() => setSubmission(value))}
                className="mt-1 text-teal-600 focus:ring-teal-500"
              />
              <span className="text-sm text-gray-700">
                {label}
                <span className="block text-xs text-gray-500">{hint}</span>
              </span>
            </label>
          ))}
        </fieldset>
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={includeCancelled}
            onChange={(e) => setFilter(() => setIncludeCancelled(e.target.checked))}
            className="mt-0.5 rounded text-teal-600 focus:ring-teal-500"
          />
          <span className="text-sm text-gray-700">
            Include cancelled claims
            <span className="block text-xs text-gray-500">They go in their own file, zipped with the live one</span>
          </span>
        </label>
      </div>
      <div>
        <p className="text-sm font-medium text-gray-700 mb-1">Date claim opened</p>
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-xs text-gray-500" htmlFor="all-opened-from">From</label>
          <input
            id="all-opened-from"
            type="date"
            value={openedFrom}
            max={openedTo || undefined}
            onChange={(e) => setFilter(() => setOpenedFrom(e.target.value))}
            className="px-2 py-1 border border-gray-300 rounded-md text-sm"
          />
          <label className="text-xs text-gray-500" htmlFor="all-opened-to">To</label>
          <input
            id="all-opened-to"
            type="date"
            value={openedTo}
            min={openedFrom || undefined}
            onChange={(e) => setFilter(() => setOpenedTo(e.target.value))}
            className="px-2 py-1 border border-gray-300 rounded-md text-sm"
          />
          {(openedFrom || openedTo) && (
            <Button variant="ghost" size="sm" onClick={() => setFilter(() => { setOpenedFrom(''); setOpenedTo(''); })}>
              Clear
            </Button>
          )}
        </div>
        <p className="mt-1 text-xs text-gray-500">Leave both blank for any date. Both days are included.</p>
        <p className="mt-4 text-sm text-gray-800">
          {allCountsLoading ? (
            <span className="text-gray-500">Counting…</span>
          ) : allCounts ? (
            <>
              <span className="font-semibold tabular-nums">{formatNumber(allCounts.total)}</span> claims will be exported
              {includeCancelled && allCounts.cancelled > 0 ? (
                <> ({formatNumber(allCounts.live)} live and {formatNumber(allCounts.cancelled)} cancelled, in two files)</>
              ) : null}.
            </>
          ) : null}
        </p>
      </div>
    </div>
  );

  const recentJobsList = recentJobs.length > 0 ? (
    <div className="mt-4 text-xs text-gray-600 border-t border-gray-100 pt-3">
      <p className="font-medium text-gray-700 mb-1">Recent all-lenders exports</p>
      <ul className="space-y-1">
        {recentJobs.slice(0, 3).map((j) => (
          <li key={j.jobId}>
            {formatStamp(j.startedAt)} —{' '}
            {j.status === 'done' && j.downloadUrl ? (
              <>
                <a href={j.downloadUrl} target="_blank" rel="noopener noreferrer" className="text-teal-700 underline">{j.filename}</a>
                {j.rows != null && <> ({formatNumber(j.rows)} claims)</>}
                {j.expiresAt && <> · link valid until {formatStamp(j.expiresAt)}</>}
              </>
            ) : j.status === 'failed' ? (
              <span className="text-red-700">failed{j.error ? ` — ${j.error}` : ''}</span>
            ) : (
              <>{ALL_JOB_STAGES[j.stage] ?? j.stage} {formatNumber(j.written)}{j.total ? ` of ${formatNumber(j.total)}` : ''}</>
            )}
          </li>
        ))}
      </ul>
    </div>
  ) : null;

  const visibleSteps = allLenders ? STEPS.filter((s) => s.n !== 2) : STEPS;

  const stepper = (
    <ol className="flex flex-wrap items-stretch gap-2 mb-6" aria-label="Export steps">
      {visibleSteps.map(({ n, label }, index) => {
        const state = n === step ? 'current' : n < step ? 'done' : n <= furthestStep ? 'visited' : 'todo';
        const summary = n !== step ? stepSummary(n) : '';
        // !bg-*: index.css has a `button { background-color: transparent }` rule after
        // Tailwind's utilities, which would otherwise wipe these colours.
        const boxClass = state === 'current'
          ? 'border-[#01b8c0] text-white'
          : state === 'done'
            ? 'border-blue-300 !bg-blue-50 text-blue-900 hover:!bg-blue-100'
            : state === 'visited'
              ? 'border-gray-200 !bg-white hover:!bg-gray-50'
              : 'border-dashed border-gray-200 !bg-gray-50 text-gray-400 cursor-not-allowed';
        return (
          <li key={n} className="flex-1 min-w-[170px] flex items-center gap-2">
            <button
              type="button"
              onClick={() => goTo(n)}
              disabled={!canVisit(n)}
              aria-current={n === step ? 'step' : undefined}
              data-state={state}
              className={`flex-1 h-full text-left rounded-lg border px-3 py-2 ${boxClass}`}
              // the current step is the sidebar's bottom teal (#01b8c0, Header.css); inline
              // so the global `button { background-color: transparent }` reset can't wipe it
              style={state === 'current' ? { backgroundColor: '#01b8c0' } : undefined}
            >
              <span className="flex items-center justify-between text-xs uppercase tracking-wide">
                <span className={state === 'done' ? 'text-blue-700' : state === 'current' ? 'text-white/80' : 'text-gray-500'}>Step {index + 1}</span>
                {state === 'done' && <span className="text-blue-700 font-semibold" aria-label="completed">&#10003; Done</span>}
              </span>
              <span className="block text-sm font-semibold">{label}</span>
              {summary && <span className={`block text-xs truncate ${state === 'current' ? 'text-white/80' : 'text-gray-600'}`} title={summary}>{summary}</span>}
            </button>
            {index < visibleSteps.length - 1 && (
              <svg
                aria-hidden="true"
                width="36"
                height="36"
                viewBox="0 0 24 24"
                fill="none"
                stroke={state === 'done' ? '#3b82f6' : '#d1d5db'}
                strokeWidth="3.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="shrink-0"
              >
                <path d="M4 12h16M13 5l7 7-7 7" />
              </svg>
            )}
          </li>
        );
      })}
    </ol>
  );

  // ------------------------------------------------------------------
  // Step 1: lenders
  // ------------------------------------------------------------------
  const stepLenders = (
    <>
      <Card title="Lenders" className="mb-6">
        <label htmlFor="lender-names" className="block text-sm font-medium text-gray-700 mb-1">
          Lender name(s)
        </label>
        <div className="relative">
          <input
            id="lender-names"
            ref={inputRef}
            type="text"
            value={namesInput}
            onChange={(e) => {
              setNamesInput(e.target.value);
              setCaret(e.target.selectionStart);
              setShowSuggestions(true);
              setActiveSuggestion(-1);
            }}
            onSelect={(e) => setCaret((e.target as HTMLInputElement).selectionStart)}
            onKeyDown={handleNamesKeyDown}
            onFocus={() => {
              // only cancel a pending blur-close; typing (not focus)
              // opens the dropdown, so it can't cover the Resolve button
              // and swallow its click after a refocus
              if (blurTimer.current !== null) {
                window.clearTimeout(blurTimer.current);
                blurTimer.current = null;
              }
            }}
            onBlur={() => {
              blurTimer.current = window.setTimeout(() => {
                setShowSuggestions(false);
                blurTimer.current = null;
              }, 150);
            }}
            placeholder="Start typing a lender — e.g. Oodle. Separate multiple lenders with commas"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            autoComplete="off"
            role="combobox"
            aria-expanded={suggestionsOpen}
            aria-controls="lender-suggestions"
          />
          {suggestionsOpen && (
            <ul
              id="lender-suggestions"
              role="listbox"
              /* preventDefault here too so grabbing the list's scrollbar
                 doesn't blur the input and close the list mid-scroll */
              onMouseDown={(e) => e.preventDefault()}
              className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-72 overflow-y-auto"
            >
              {suggestions.map((option, index) => (
                <li
                  key={`${option.type}-${option.name}`}
                  role="option"
                  aria-selected={index === activeSuggestion}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    selectSuggestion(option);
                  }}
                  onMouseEnter={() => setActiveSuggestion(index)}
                  className={`px-3 py-2 text-sm cursor-pointer flex items-baseline justify-between gap-3 ${
                    index === activeSuggestion ? 'bg-teal-50' : ''
                  }`}
                >
                  <span className="text-gray-800">
                    {option.name}
                    {option.type === 'alias' && option.canonicalName && option.canonicalName !== option.name && (
                      <span className="ml-2 text-xs text-gray-400">alias of {option.canonicalName}</span>
                    )}
                  </span>
                  <span className="text-xs text-gray-400 truncate">{option.email}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <p className="mt-1 text-xs text-gray-500">
          The system finds each lender's email in the mapping, then includes every other lender name using that
          same email (canonicals and aliases), plus any claim spellings that look like they belong but aren't mapped.
          Pick several lenders to export them in one go — each lender gets its own file.
        </p>
        <div className="mt-3">
          <Button onClick={handleResolve} disabled={names.length === 0 || previewLoading} variant="primary">
            {previewLoading ? 'Resolving…' : 'Resolve lender'}
          </Button>
        </div>
      </Card>

      <Card title="Or export every lender" className="mb-6">
        <p className="text-sm text-gray-600 mb-3">
          One export across all lenders: every claim spelling, mapped or not. It is built in the background and the
          file is kept for 24 hours. Two columns are added at the front of every row — the lender on the claim and the
          mapped lender — so the file stays attributable. Live and cancelled claims go in separate files.
          Marking as bulk-submitted is not available for an all-lenders export.
        </p>
        <Button onClick={handleAllLenders} disabled={allCountsLoading || previewLoading} variant={allLenders ? 'primary' : 'secondary'}>
          {allCountsLoading ? 'Counting…' : 'All lenders'}
        </Button>
        {allLenders && allOverviewTable && <div className="mt-4">{allOverviewTable}</div>}
        {allLenders && allFilterControls}
        {recentJobsList}
      </Card>

      {preview && preview.unresolved.length > 0 && (
        <Card className="mb-6">
          {preview.unresolved.map((u) => (
            <div key={u.input} className="text-sm text-amber-700">
              <p className="font-medium">"{u.input}" could not be resolved to one lender.</p>
              {u.choices.length > 0 ? (
                <ul className="mt-1 ml-4 list-disc text-xs text-gray-600">
                  {u.choices.map((c) => (
                    <li key={`${c.name}-${c.email}`}>
                      {c.name} <span className="text-gray-400">({c.email})</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-gray-500">No match in the lender mapping — check the spelling.</p>
              )}
            </div>
          ))}
        </Card>
      )}

      {families.length > 1 && (
        <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded-lg mb-6 text-sm">
          {distinctEmails.size > 1
            ? `These ${families.length} lenders use different email addresses on file, so you'll get one file per lender (zipped together).`
            : `These lenders share one email address on file, so they'll be exported together.`}
          <ul className="mt-1 ml-4 list-disc text-xs">
            {families.map((f) => (
              <li key={f.email || f.canonicals.join('|')}>
                {f.canonicals[0]} — <span className="font-mono">{f.email || 'no email on file'}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {families.map((family) => (
        <Card key={family.email || family.canonicals.join('|')} className="mb-6">
          {familyHeading(family)}
          {family.aliases.length > 0 && (
            <p className="text-xs text-gray-500 mb-3">Aliases: {family.aliases.join(' · ')}</p>
          )}
          {spellingTable(family)}
          <p className="mt-3 text-sm text-gray-600">
            Mapped total: <strong>{formatNumber(family.totals.live)}</strong> live
            {family.totals.cancelled > 0 && (
              <span className="text-gray-500"> · {formatNumber(family.totals.cancelled)} cancelled</span>
            )}
          </p>
        </Card>
      ))}

      {(preview || allLenders) && (
        <div className="flex items-center gap-4">
          <Button onClick={next} disabled={!step1Ready || allCountsLoading} variant="primary">
            {allLenders ? 'Next: Layout' : 'Next: Claims'}
          </Button>
          {!step1Ready && (
            <span className="text-xs text-gray-500">
              {allLenders ? 'No claims match these filters.' : 'Tick at least one lender spelling above.'}
            </span>
          )}
        </div>
      )}
    </>
  );

  // ------------------------------------------------------------------
  // Step 2: claims
  // ------------------------------------------------------------------
  const stepClaims = (
    <>
      <Card title="Which claims" className="mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <fieldset>
              <legend className="text-sm font-medium text-gray-700 mb-1">Bulk submission</legend>
              {([
                ['unsubmitted', 'Not yet bulk-submitted', 'The usual pick for a new submission file'],
                ['all', 'Everything, bulk-submitted or not', 'A full live export of these lenders'],
                ['submitted', 'Already bulk-submitted only', 'The reconciliation view - pairs well with the Lender responses sheet'],
              ] as [SubmissionFilter, string, string][]).map(([value, label, hint]) => (
                <label key={value} className="flex items-start gap-2 cursor-pointer mb-1.5">
                  <input
                    type="radio"
                    name="submission-filter"
                    checked={submission === value}
                    onChange={() => setFilter(() => setSubmission(value))}
                    className="mt-1 text-teal-600 focus:ring-teal-500"
                  />
                  <span className="text-sm text-gray-700">
                    {label}
                    <span className="block text-xs text-gray-500">{hint}</span>
                  </span>
                </label>
              ))}
            </fieldset>
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={includeCancelled}
                onChange={(e) => setFilter(() => setIncludeCancelled(e.target.checked))}
                className="mt-0.5 rounded text-teal-600 focus:ring-teal-500"
              />
              <span className="text-sm text-gray-700">
                Include cancelled claims
                <span className="block text-xs text-gray-500">Normally off — bulk submissions are for live claims</span>
              </span>
            </label>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-700 mb-1">Date claim opened</p>
            <div className="flex flex-wrap items-center gap-2">
              <label className="text-xs text-gray-500" htmlFor="opened-from">From</label>
              <input
                id="opened-from"
                type="date"
                value={openedFrom}
                max={openedTo || undefined}
                onChange={(e) => setFilter(() => setOpenedFrom(e.target.value))}
                className="px-2 py-1 border border-gray-300 rounded-md text-sm"
              />
              <label className="text-xs text-gray-500" htmlFor="opened-to">To</label>
              <input
                id="opened-to"
                type="date"
                value={openedTo}
                min={openedFrom || undefined}
                onChange={(e) => setFilter(() => setOpenedTo(e.target.value))}
                className="px-2 py-1 border border-gray-300 rounded-md text-sm"
              />
              {(openedFrom || openedTo) && (
                <Button variant="ghost" size="sm" onClick={() => setFilter(() => { setOpenedFrom(''); setOpenedTo(''); })}>
                  Clear
                </Button>
              )}
            </div>
            <p className="mt-1 text-xs text-gray-500">Leave both blank for any date. Both days are included.</p>
          </div>
        </div>
      </Card>

      <Card title="Claims that match" className="mb-6">
        {countsStale ? (
          <div className="flex items-center gap-3">
            <span className="text-sm text-amber-700">Filters changed — refresh the counts.</span>
            <Button onClick={refreshCounts} disabled={previewLoading} variant="secondary" size="sm">
              {previewLoading ? 'Counting…' : 'Refresh counts'}
            </Button>
          </div>
        ) : allLenders ? (
          allCountsLoading ? <span className="text-sm text-gray-500">Counting…</span> : allCountsTable
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500">
                <th className="py-1 pr-4 font-medium">Lender</th>
                <th className="py-1 pr-4 font-medium">Email on file</th>
                <th className="py-1 font-medium text-right">Claims</th>
              </tr>
            </thead>
            <tbody>
              {families.map((f) => (
                <tr key={f.email || f.canonicals.join('|')} className="border-t border-gray-100">
                  <td className="py-1.5 pr-4">{f.canonicals[0]}</td>
                  <td className="py-1.5 pr-4 font-mono text-xs">{f.email || '—'}</td>
                  <td className="py-1.5 text-right tabular-nums">{formatNumber(familyEligible(f))}</td>
                </tr>
              ))}
              <tr className="border-t border-gray-200 font-semibold">
                <td className="py-1.5 pr-4" colSpan={2}>Total</td>
                <td className="py-1.5 text-right tabular-nums">{formatNumber(selectedCounts.eligible)}</td>
              </tr>
            </tbody>
          </table>
        )}
      </Card>

      <div className="flex items-center gap-3">
        <Button variant="secondary" onClick={() => goTo(1)}>Back</Button>
        <Button onClick={next} disabled={countsStale || previewLoading || allCountsLoading || eligibleTotal === 0} variant="primary">Next: Layout</Button>
        {!countsStale && !allCountsLoading && eligibleTotal === 0 && (
          <span className="text-xs text-amber-700">No claims match these filters.</span>
        )}
      </div>
    </>
  );

  // ------------------------------------------------------------------
  // Step 3: layout
  // ------------------------------------------------------------------
  const groupedFields = useMemo(() => {
    const groups: { group: string; items: FieldOption[] }[] = [];
    fields.forEach((f) => {
      let g = groups.find((x) => x.group === f.group);
      if (!g) {
        g = { group: f.group, items: [] };
        groups.push(g);
      }
      g.items.push(f);
    });
    return groups;
  }, [fields]);

  const stepLayout = (
    <>
      <Card title="Layout" className="mb-6">
        <div className="flex flex-wrap gap-6 mb-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="radio" name="layout-mode" checked={mode === 'template'} onChange={() => { setMode('template'); setConfirmed(false); }} />
            <span className="text-sm text-gray-700">Lender template</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="radio" name="layout-mode" checked={mode === 'custom'} onChange={() => { setMode('custom'); setConfirmed(false); }} />
            <span className="text-sm text-gray-700">Choose my own fields</span>
          </label>
        </div>

        {mode === 'template' && (
          <div>
            <label htmlFor="template-select" className="block text-sm font-medium text-gray-700 mb-1">Template</label>
            <select
              id="template-select"
              value={templateKey}
              onChange={(e) => { setTemplateKey(e.target.value); setConfirmed(false); }}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm"
            >
              <option value="">— pick a lender template —</option>
              {templates.map((t) => (
                <option key={t.key} value={t.key}>{t.name}</option>
              ))}
            </select>
            {template && (
              <div className="mt-3">
                {template.note && <p className="text-xs text-amber-700 mb-2">{template.note}</p>}
                <p className="text-xs text-gray-500 mb-1">
                  {template.columns.length} columns, in the lender's order. Blank ones are for the lender or data we don't hold.
                </p>
                <div className="max-h-64 overflow-y-auto border border-gray-100 rounded-md">
                  <table className="w-full text-xs">
                    <tbody>
                      {template.columns.map((c, i) => (
                        <tr key={`${i}-${c.header}`} className={`border-t border-gray-100 ${c.filled ? '' : 'text-gray-400'}`}>
                          <td className="py-1 px-2 w-8 text-gray-400">{i + 1}</td>
                          <td className="py-1 px-2">{c.header || <em>(blank header)</em>}</td>
                          <td className="py-1 px-2 text-gray-500">{c.filled ? c.source : 'left blank'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {mode === 'custom' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-gray-700">Fields</p>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setChosenFields(fields.map((f) => f.key))}>All</Button>
                  <Button variant="ghost" size="sm" onClick={() => setChosenFields([])}>None</Button>
                </div>
              </div>
              <div className="max-h-96 overflow-y-auto border border-gray-100 rounded-md p-2 space-y-3">
                {groupedFields.map((g) => (
                  <div key={g.group}>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">{g.group}</p>
                    {g.items.map((f) => (
                      <label key={f.key} className="flex items-center gap-2 cursor-pointer py-0.5">
                        <input
                          type="checkbox"
                          checked={chosenFields.includes(f.key)}
                          onChange={() => { toggleField(f.key); setConfirmed(false); }}
                          className="rounded text-teal-600 focus:ring-teal-500"
                        />
                        <span className="text-sm text-gray-700">{f.label}</span>
                      </label>
                    ))}
                  </div>
                ))}
              </div>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Columns in order ({chosenFields.length})</p>
              {chosenFields.length === 0 ? (
                <p className="text-xs text-gray-500">Tick fields on the left. Columns appear in the order you tick them.</p>
              ) : (
                <ol className="border border-gray-100 rounded-md divide-y divide-gray-100 max-h-64 overflow-y-auto">
                  {chosenFields.map((key, i) => (
                    <li key={key} className="flex items-center gap-2 px-2 py-1 text-sm">
                      <span className="w-6 text-xs text-gray-400">{i + 1}</span>
                      <span className="flex-1">{fieldLabel(key)}</span>
                      <button type="button" className="text-xs text-gray-500 hover:text-gray-800" onClick={() => moveField(key, -1)} aria-label={`Move ${fieldLabel(key)} up`} disabled={i === 0}>↑</button>
                      <button type="button" className="text-xs text-gray-500 hover:text-gray-800" onClick={() => moveField(key, 1)} aria-label={`Move ${fieldLabel(key)} down`} disabled={i === chosenFields.length - 1}>↓</button>
                      <button type="button" className="text-xs text-red-500 hover:text-red-700" onClick={() => toggleField(key)} aria-label={`Remove ${fieldLabel(key)}`}>✕</button>
                    </li>
                  ))}
                </ol>
              )}

              <div className="mt-4 border-t border-gray-100 pt-3">
                <p className="text-sm font-medium text-gray-700 mb-2">Saved layouts</p>
                {presets.length === 0 ? (
                  <p className="text-xs text-gray-500 mb-2">No saved layouts yet.</p>
                ) : (
                  <ul className="space-y-1 mb-2">
                    {presets.map((p) => (
                      <li key={p.id} className="flex items-center gap-2 text-sm">
                        <span className="flex-1">{p.name} <span className="text-xs text-gray-400">({p.fields.length} fields)</span></span>
                        <Button variant="secondary" size="sm" onClick={() => { setChosenFields(p.fields); setConfirmed(false); }}>Load</Button>
                        <Button variant="danger" size="sm" onClick={() => deletePreset(p)} disabled={presetBusy} aria-label={`Delete ${p.name}`}>Delete</Button>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={presetName}
                    onChange={(e) => setPresetName(e.target.value)}
                    placeholder="Name this layout"
                    aria-label="Preset name"
                    className="flex-1 px-2 py-1 border border-gray-300 rounded-md text-sm"
                  />
                  <Button variant="secondary" size="sm" onClick={savePreset} disabled={presetBusy || !presetName.trim() || chosenFields.length === 0}>
                    Save layout
                  </Button>
                </div>
                {presetMessage && <p className="mt-1 text-xs text-gray-600">{presetMessage}</p>}
              </div>
            </div>
          </div>
        )}
      </Card>

      {responsesPossible && (
      <Card title="Lender responses" className="mb-6">
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={includeResponses}
            onChange={(e) => { setIncludeResponses(e.target.checked); setConfirmed(false); }}
            className="mt-0.5 rounded text-teal-600 focus:ring-teal-500"
          />
          <span className="text-sm text-gray-700">
            Add a &ldquo;Lender responses&rdquo; sheet to every file
            <span className="block text-xs text-gray-500">
              One row per response received from the lender (imported responses): received date, batch, agreement number, dates, amounts financed, commission
              figures and model, outcome, plus every extra column the lender sent. Claims without a response have no rows there.
            </span>
          </span>
        </label>
        {mode === 'custom' && (
          <p className="mt-2 text-xs text-gray-500">
            For single columns on the main sheet, tick fields under &ldquo;Latest lender response&rdquo; in the list above.
          </p>
        )}
      </Card>
      )}

      <div className="flex items-center gap-3">
        <Button variant="secondary" onClick={() => goTo(allLenders ? 1 : 2)}>Back</Button>
        <Button onClick={next} disabled={!layoutReady} variant="primary">Next: Review</Button>
        {!layoutReady && (
          <span className="text-xs text-gray-500">
            {mode === 'template' ? 'Pick a template.' : 'Tick at least one field.'}
          </span>
        )}
      </div>
    </>
  );

  // ------------------------------------------------------------------
  // Step 4: review, download, mark as sent
  // ------------------------------------------------------------------
  const reviewRow = (label: string, value: React.ReactNode, editStep: Step) => (
    <div className="flex items-start gap-3 py-2 border-t border-gray-100 first:border-t-0">
      <div className="w-28 shrink-0 text-xs font-medium uppercase tracking-wide text-gray-500 pt-0.5">{label}</div>
      <div className="flex-1 text-sm text-gray-800">{value}</div>
      <Button variant="ghost" size="sm" onClick={() => goTo(editStep)} aria-label={`Edit ${label.toLowerCase()}`}>Edit</Button>
    </div>
  );

  const stepReview = (
    <>
      <Card title="Check before you download" className="mb-6">
        {reviewRow('Lenders', allLenders ? (
          <>
            <span className="font-medium">All lenders</span> — every claim spelling, mapped or not.
            <span className="block text-xs text-gray-500">Two columns are added at the front of each row: the lender on the claim and the mapped lender.</span>
          </>
        ) : (
          <ul className="space-y-1">
            {families.map((f) => (
              <li key={f.email || f.canonicals.join('|')}>
                <span className="font-medium">{f.canonicals[0]}</span>
                {' '}<span className="font-mono text-xs text-gray-500">{f.email || 'no email on file'}</span>
                <span className="block text-xs text-gray-500">
                  {[...f.matchedSpellings, ...f.suggestedSpellings].filter((s) => selected.has(s.spelling)).map((s) => s.spelling).join(', ')}
                </span>
              </li>
            ))}
          </ul>
        ), 1)}
        {reviewRow('Claims', (
          <>
            {includeCancelled ? 'Live and cancelled claims' : 'Live claims only'}
            {`, ${SUBMISSION_LABELS[submission]}`}
            {openedFrom || openedTo
              ? `, opened ${openedFrom ? `from ${formatDay(openedFrom)}` : ''}${openedFrom && openedTo ? ' ' : ''}${openedTo ? `to ${formatDay(openedTo)}` : ''}`
              : ', opened any time'}
          </>
        ), allLenders ? 1 : 2)}
        {reviewRow('Layout', mode === 'template'
          ? <>Lender template: <span className="font-medium">{template?.name ?? templateKey}</span> ({template?.columns.length ?? '?'} columns)</>
          : <>Custom: {chosenFields.map(fieldLabel).join(', ')}</>, 3)}
        {responsesPossible && reviewRow('Responses', responsesWanted
          ? <>A &ldquo;Lender responses&rdquo; sheet is added to each file (one row per response received){claimsList && typeof claimsList.withResponses === 'number' ? <> &mdash; {formatNumber(claimsList.withResponses)} of the {formatNumber(claimsList.total)} claims have one</> : null}</>
          : <>No lender responses sheet</>, 3)}
        {reviewRow('Files', allLenders ? (
          allCountsLoading ? 'Counting claims…' : allCounts ? (
            <ul className="space-y-0.5">
              <li><span className="font-medium tabular-nums">{formatNumber(allCounts.live)}</span> live claims — one file</li>
              {includeCancelled && (
                <li><span className="font-medium tabular-nums">{formatNumber(allCounts.cancelled)}</span> cancelled claims — a second file</li>
              )}
              <li className="pt-1 border-t border-gray-100">
                <span className="font-semibold tabular-nums">{formatNumber(allCounts.total)}</span> claims in total
                {includeCancelled ? ', two files zipped together' : ', one file'}
                {responsesWanted ? ', each with a Lender responses sheet' : ''}
              </li>
            </ul>
          ) : <Button variant="secondary" size="sm" onClick={loadAllCounts}>Count claims</Button>
        ) : (
          claimsLoading ? 'Counting claims…' : claimsList ? (
            <ul className="space-y-0.5">
              {claimsList.families.map((f) => (
                <li key={f.email || f.lenderName}>
                  <span className="font-medium tabular-nums">{formatNumber(f.count)}</span> claims for {f.lenderName}
                  {responsesWanted && typeof f.withResponses === 'number' && f.count > 0 && (
                    <span className="text-xs text-gray-500"> ({formatNumber(f.withResponses)} with lender responses)</span>
                  )}
                  {f.count === 0 && <span className="text-xs text-amber-700"> (no file — nothing matched)</span>}
                </li>
              ))}
              <li className="pt-1 border-t border-gray-100">
                <span className="font-semibold tabular-nums">{formatNumber(claimsList.total)}</span> claims in total
                {claimsList.families.filter((f) => f.count > 0).length > 1 ? ', one file per lender, zipped' : ''}
              </li>
            </ul>
          ) : <Button variant="secondary" size="sm" onClick={loadClaimsList}>Count claims</Button>
        ), allLenders ? 1 : 2)}
      </Card>

      <Card className="mb-6">
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
            disabled={allLenders ? !allCounts || allCounts.total === 0 : !claimsList || claimsList.total === 0}
            className="mt-0.5 rounded text-teal-600 focus:ring-teal-500"
          />
          <span className="text-sm text-gray-700">
            I've checked the lenders, claims and layout above and want to {allLenders ? 'build' : 'download'} this export.
          </span>
        </label>
        <div className="mt-4 flex items-center gap-4 flex-wrap">
          <Button variant="secondary" onClick={() => goTo(3)}>Back</Button>
          {allLenders ? (
            <Button onClick={handleBuildAll} disabled={!confirmed || !allCounts || allCounts.total === 0 || allStarting || allJobBusy} variant="primary">
              {allStarting ? 'Starting…' : allJobBusy ? 'Building…' : `Build export for ${allCounts ? formatNumber(allCounts.total) : ''} claims`}
            </Button>
          ) : (
            <Button onClick={handleDownload} disabled={!confirmed || !claimsList || claimsList.total === 0 || downloading} variant="primary">
              {downloading ? 'Building export…' : `Download ${claimsList ? formatNumber(claimsList.total) : ''} claims`}
            </Button>
          )}
          {downloadResult && <span className="text-sm text-green-700">{downloadResult}</span>}
        </div>
        <p className="mt-2 text-xs text-gray-500">
          {allLenders
            ? 'The export is built in the background and stored for 24 hours. Nothing is marked or sent to anyone.'
            : 'Downloading changes nothing on the claims and sends nothing to anyone.'}
        </p>
      </Card>

      {allLenders && allJob && (
        <Card title="Building the export" className="mb-6">
          {allJobBusy && (
            <div>
              <ProgressBar value={allJob.written} max={allJob.total || Math.max(allJob.written, 1)} />
              <p className="mt-1 text-xs text-gray-600">
                {ALL_JOB_STAGES[allJob.stage] ?? allJob.stage} {formatNumber(allJob.written)}
                {allJob.total ? ` of ${formatNumber(allJob.total)} claims` : ' claims'}
              </p>
              <p className="mt-2 text-xs text-gray-500">
                You can leave this page. The file is kept for 24 hours and the latest builds are listed on the Lenders step when you come back.
              </p>
            </div>
          )}
          {allJob.status === 'done' && (
            <div className="text-sm text-gray-700 space-y-2">
              <p>
                <span className="font-semibold tabular-nums">{formatNumber(allJob.rows ?? allJob.written)}</span> claims written to{' '}
                <span className="font-medium">{allJob.filename}</span>{allJob.sizeBytes ? ` (${formatBytes(allJob.sizeBytes)})` : ''}.
              </p>
              <a
                href={allJob.downloadUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-4 py-2 rounded-md bg-teal-600 text-white text-sm font-medium hover:bg-teal-700"
              >
                Download {allJob.filename}
              </a>
              <p className="text-xs text-gray-500">
                Link valid until {formatStamp(allJob.expiresAt)}. Nothing has been marked or sent.
              </p>
            </div>
          )}
          {allJob.status === 'failed' && (
            <p className="text-sm text-red-700">The export failed{allJob.error ? `: ${allJob.error}` : ''}. Start it again.</p>
          )}
        </Card>
      )}

      {!allLenders && downloadedFor && downloadedFor.total > 0 && (
        <Card title="Mark as bulk-submitted" className="mb-6 border-2 border-purple-200">
          {!markSummary ? (
            <>
              <p className="text-sm text-gray-700 mb-3">
                Once this export has gone to the lender, mark the {formatNumber(downloadedFor.total)} claims as bulk-submitted so they
                show as "Bulk Submitted" and aren't exported again. Nothing is emailed to anyone.
              </p>
              {marking ? (
                <div>
                  <ProgressBar value={markProgress.done} max={markProgress.total} />
                  <p className="mt-1 text-xs text-gray-600">
                    Marking {formatNumber(markProgress.done)} of {formatNumber(markProgress.total)}…
                  </p>
                </div>
              ) : (
                <Button onClick={() => setMarkConfirmOpen(true)} variant="primary" className="!bg-purple-700 hover:!bg-purple-800">
                  Mark {formatNumber(downloadedFor.total)} claims as bulk-submitted
                </Button>
              )}
            </>
          ) : (
            <div className="text-sm text-gray-700 space-y-1">
              <p><span className="font-semibold">{formatNumber(markSummary.marked.length)}</span> claims marked as bulk-submitted{markSummary.sentDate ? ` on ${formatDay(markSummary.sentDate.slice(0, 10))}` : ''}.</p>
              {markSummary.alreadySubmitted.length > 0 && <p>{formatNumber(markSummary.alreadySubmitted.length)} were already marked and were left as they were.</p>}
              {markSummary.cancelled.length > 0 && <p>{formatNumber(markSummary.cancelled.length)} are now cancelled and were skipped.</p>}
              {markSummary.notInSelection.length > 0 && <p>{formatNumber(markSummary.notInSelection.length)} no longer match the lender and were skipped.</p>}
              {markSummary.conflict.length > 0 && <p className="text-amber-700">{formatNumber(markSummary.conflict.length)} changed while marking — try again for those: {markSummary.conflict.slice(0, 10).join(', ')}{markSummary.conflict.length > 10 ? '…' : ''}</p>}
              {markSummary.notFound.length > 0 && <p className="text-amber-700">{formatNumber(markSummary.notFound.length)} could not be found.</p>}
              {markSummary.failed.length > 0 && <p className="text-red-700">{formatNumber(markSummary.failed.length)} failed to save: {markSummary.failed.slice(0, 10).join(', ')}{markSummary.failed.length > 10 ? '…' : ''}</p>}
            </div>
          )}
        </Card>
      )}

      {markConfirmOpen && downloadedFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true" aria-labelledby="mark-confirm-title">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
            <h3 id="mark-confirm-title" className="text-lg font-semibold text-gray-900 mb-2">Mark as sent to lender?</h3>
            <p className="text-sm text-gray-700 mb-3">
              This marks <strong>{formatNumber(downloadedFor.total)}</strong> claims as bulk-submitted:
            </p>
            <ul className="text-sm text-gray-700 mb-3 ml-4 list-disc">
              {downloadedFor.families.filter((f) => f.count > 0).map((f) => (
                <li key={f.email || f.lenderName}>
                  {formatNumber(f.count)} to <span className="font-medium">{f.lenderName}</span>{' '}
                  <span className="font-mono text-xs text-gray-500">{f.email || 'no email on file'}</span>
                </li>
              ))}
            </ul>
            <p className="text-xs text-gray-500 mb-4">
              Each claim gets today's date as its submission date and a note in its audit log. Nothing is emailed. Claims already marked are left alone.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setMarkConfirmOpen(false)}>Cancel</Button>
              <Button variant="primary" className="!bg-purple-700 hover:!bg-purple-800" onClick={handleMarkSubmitted}>Yes, mark {formatNumber(downloadedFor.total)} claims</Button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <PageHeader
        title="Lender Export"
        description="Export claims for one or more lenders — in the lender's own template or your own layout"
      />

      {stepper}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 flex items-start gap-2">
          <p className="text-sm font-medium flex-1">{error}</p>
          <Button variant="danger" size="sm" onClick={() => setError('')} aria-label="Dismiss error">
            Dismiss
          </Button>
        </div>
      )}

      {step === 1 && stepLenders}
      {step === 2 && stepClaims}
      {step === 3 && stepLayout}
      {step === 4 && stepReview}
    </div>
  );
};

export default LenderClaimsExport;
